/*
  The advanced-search panel inside the cert-lookup hero (§3.4, D22, task 6a's Select).

  **It themes.** It is one of D22's four inner panels, so it follows the page theme like
  any other card — only the hero's photo backdrop and the overlay text sitting directly on
  it are fixed dark. Everything here is a token, and `text-white` is never one of them: it
  is pointed at `--pcg-fg` (tailwind.config.js) and would go near-black on paper. That is
  also why the focus ring is the themed `FOCUS_RING` from components/styles — the surface
  under these controls is a page surface, not the AppBar's greys or the Footer's gold.

  Nine selects and two text fields, all off MUI:

  - the nine `FormControl` / `InputLabel` / `Select` / `MenuItem` stacks are one shared
    `components/Select` each (a native <select>, so keyboard, type-ahead and the mobile
    wheel picker come free).
  - the empty choice is a real, selectable option — `ANY` — not Select's `placeholder`,
    which renders a DISABLED first option. A visitor who picks a game has to be able to
    get back to "any game", exactly as FilterGradeDDL reasons about "All grades".
  - the two `TextField`s are plain inputs sharing CONTROL_BASE with everything else.
  - the `Grid` is a Tailwind grid: one column at 375px, two from `sm`, with Game across
    both. No fixed widths, so there is nothing to overflow the panel.

  The two Emotion `css` blocks are gone with no behaviour hidden in them: `margin-right:
  16px` on Clear, and `color: white` on the contained Search button. (Task 15's warning is
  about a `css` block on an SVG beating a presentation attribute — worth the read, but
  there is no SVG in this file; the shared Select owns its own chevron.)

  Search semantics are unchanged: `onSearch` receives only non-empty fields, and the hero
  intersects them (`useOr: false`). Choosing a game clears Set Name and Finish, since both
  lists come from that game.
*/

import { useState } from 'react';

import { useGetSearchOptions } from 'services/pcgPublic';

import { normaliseCertNumber } from 'lib';

import Select, { SelectOption } from 'components/Select';
import { CONTROL_BASE, CONTROL_LABEL, FOCUS_RING } from 'components/styles';

import { GRADES } from 'constants/index';

/* Says what choosing it does, rather than what it is not ("None"). */
const ANY: SelectOption = { value: '', label: 'Any' };

/* The Gold 10.0 shortcut: one choice that fills in all five perfect sub-grades. */
const GOLD_10 = 'Gold 10.0';

const toOptions = (values: readonly string[]): SelectOption[] =>
  values.map((value) => ({ value, label: value }));

const GRADE_OPTIONS = [ANY, ...toOptions(GRADES)];

const BUTTON_BASE =
  `rounded-md px-4 py-2 text-sm font-semibold ${FOCUS_RING}`;

/*
  Gold is the FILL token here with black on it (D20) — 11.4:1, and the same in both themes.

  `skin-action` marks it as THE primary action of the page, and it is the only place that
  marker may be used: gilded gives it the one gradient and the one pill on the site
  (src/skins/gilded.css), and a second one anywhere would stop this one meaning "the thing
  to press". It styles nothing in navy.
*/
const SEARCH_BUTTON = `skin-action ${BUTTON_BASE} bg-pcg-gold text-black hover:bg-pcg-goldDark`;

const CLEAR_BUTTON =
  `skin-control skin-ghost ${BUTTON_BASE} border border-pcg-border text-pcg-goldText hover:bg-pcg-raised`;

const SearchCardsAdvanced = (props: { onSearch: any } ) => {
  const { onSearch } = props;

  const searchOptions = useGetSearchOptions();
  const { options, gradeNames } = searchOptions;

  const [gradedId, setGradedId] = useState('');
  const [cardName, setCardName] = useState('');

  const [game, setGame] = useState('');
  const [set, setSet] = useState('');
  const [finish, setFinish] = useState('');

  const [grade, setGrade] = useState('');
  const [finalGrade, setFinalGrade] = useState('');
  const [centerGrade, setCenterGrade] = useState('');
  const [surfaceGrade, setSurfaceGrade] = useState('');
  const [cornersGrade, setCornersGrade] = useState('');
  const [edgesGrade, setEdgesGrade] = useState('');

  const handleGameChange = (game: string) => {
    setSet('');
    setFinish('');
    setGame(game);
  }

  const handleSetFinalGrade = (grade: string) => {
    setGrade('');
    if (grade === GOLD_10) {
      setFinalGrade('10');
      setCenterGrade('10');
      setSurfaceGrade('10');
      setCornersGrade('10');
      setEdgesGrade('10');
    } else {
      setFinalGrade(grade);
    }
  }

  const handleSetGrade = (grade: string) => {
    setFinalGrade('');
    setGrade(grade);
  }

  const handleClear = () => {
    setGradedId('');
    setCardName('');
    handleGameChange('');
    setGrade('');
    setFinalGrade('');
    setCenterGrade('');
    setSurfaceGrade('');
    setCornersGrade('');
    setEdgesGrade('');
    onSearch({});
  }

  const handleSearch = () => {
    // Map advanced search fields to v2 API params
    const searchParams: any = {};
    /* `53600`, `#53600` and `PCG 53600` all mean slab 000053600 (D26, Robert, 2026-10-20). */
    if (gradedId) searchParams.gradedId = normaliseCertNumber(gradedId);
    if (cardName) searchParams.cardName = cardName;
    if (game) searchParams.gameCode = game;
    if (set) searchParams.setName = set;
    if (finish) searchParams.finish = finish;
    if (grade) searchParams.grade = grade;
    if (finalGrade) searchParams.finalGrade = finalGrade;
    if (centerGrade) searchParams.centeringGrade = centerGrade;
    if (surfaceGrade) searchParams.surfaceGrade = surfaceGrade;
    if (cornersGrade) searchParams.cornersGrade = cornersGrade;
    if (edgesGrade) searchParams.edgesGrade = edgesGrade;

    onSearch(searchParams);
  }

  const setOptions = options[game]?.sets || [];
  const finishOptions = options[game]?.finish || [];

  const gameSelectOptions = [ANY, ...toOptions(Object.keys(options))];

  return (
    <div className="grid grid-cols-1 gap-3 p-2 sm:grid-cols-2">
      <div className="flex flex-col gap-1">
        <label htmlFor="gradedId" className={CONTROL_LABEL}>
          Graded ID
        </label>
        <input
          id="gradedId"
          name="gradedId"
          type="text"
          autoComplete="off"
          value={gradedId}
          onChange={(event) => setGradedId(event.target.value)}
          className={`w-full px-3 py-2 ${CONTROL_BASE} placeholder:text-pcg-textDim ${FOCUS_RING}`}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="cardName" className={CONTROL_LABEL}>
          Card Name
        </label>
        <input
          id="cardName"
          name="cardName"
          type="text"
          autoComplete="off"
          value={cardName}
          onChange={(event) => setCardName(event.target.value)}
          className={`w-full px-3 py-2 ${CONTROL_BASE} placeholder:text-pcg-textDim ${FOCUS_RING}`}
        />
      </div>

      {/* New Zealand Mint is already gone from `options` — the filter is in the hook (D16). */}
      <Select
        id="game"
        label="Game"
        value={game}
        options={gameSelectOptions}
        onChange={handleGameChange}
        className="sm:col-span-2"
      />

      {/*
        Both lists are the chosen game's, so with no game chosen they offer Any alone.
        Left ENABLED rather than disabled: a disabled control is not focusable, and the
        panel's contract (task 10) is that every control in it is reachable when it is open.
      */}
      <Select
        id="setName"
        label="Set Name"
        value={set}
        options={[ANY, ...toOptions(setOptions)]}
        onChange={setSet}
      />

      <Select
        id="finish"
        label="Finish"
        value={finish}
        options={[ANY, ...toOptions(finishOptions)]}
        onChange={setFinish}
      />

      {/* h3: the hero's own heading is the h2 above this panel. */}
      <h3 className="font-display text-base font-bold text-pcg-text sm:col-span-2">
        Grades
      </h3>

      <Select
        id="finalGrade"
        label="Final Grade"
        value={finalGrade}
        options={[ANY, { value: GOLD_10, label: GOLD_10 }, ...toOptions(GRADES)]}
        onChange={handleSetFinalGrade}
      />

      <Select
        id="grade"
        label="Grade"
        value={grade}
        options={[ANY, ...toOptions(gradeNames)]}
        onChange={handleSetGrade}
      />

      <Select
        id="centerGrade"
        label="Center Grade"
        value={centerGrade}
        options={GRADE_OPTIONS}
        onChange={setCenterGrade}
      />

      <Select
        id="surfaceGrade"
        label="Surface Grade"
        value={surfaceGrade}
        options={GRADE_OPTIONS}
        onChange={setSurfaceGrade}
      />

      <Select
        id="cornersGrade"
        label="Corners Grade"
        value={cornersGrade}
        options={GRADE_OPTIONS}
        onChange={setCornersGrade}
      />

      <Select
        id="edgesGrade"
        label="Edges Grade"
        value={edgesGrade}
        options={GRADE_OPTIONS}
        onChange={setEdgesGrade}
      />

      <div className="flex justify-end gap-3 sm:col-span-2">
        <button type="button" onClick={handleClear} className={CLEAR_BUTTON}>
          Clear
        </button>
        <button type="button" onClick={handleSearch} className={SEARCH_BUTTON}>
          Search
        </button>
      </div>
    </div>
  );
};

export default SearchCardsAdvanced;
